import * as React from 'react';
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import InfoIcon from '@mui/icons-material/Info';
import { styled } from '@mui/material/styles';
import _ from 'lodash';
import Input from '../input';
import { Tooltip } from '@mui/material';

const CustomInfoIcon = styled(InfoIcon)`
  color: #283665;
`

const CustomTableCell = ({column, row, handleDirtyRows, defaultRow, propsViewLink}) => {
    const [isEditModeOn, setEditModeStatus] = useState(false)
    const tableCellRef = useRef()
    const navigate = useNavigate();
    const value = _.get(row, column.dataKey);
    if (isEditModeOn) {
        return (
            <TableCell key={column.dataKey} align={column.align} onBlur={() => {
                setEditModeStatus(false)
                if (!_.isEqual(value, tableCellRef.current.value))
                    handleDirtyRows(row._id, row, column.dataKey, tableCellRef.current.value)
            }}>
                {column.editComponent ? column.editComponent({value, defaultRow, ref: tableCellRef}) :
                    <Input
                        ref={tableCellRef}
                        autoFocus={true}
                        defaultValue={value}
                        type={column?.type ?? "text"}
                        variant={"outlined"}
                        InputLabelProps={{
                            shrink: false,
                        }}
                        style={{width: "auto"}}
                        size={"small"}
                    />
                }
            </TableCell>
        )
    } else {
        return (
            <TableCell key={column.dataKey} align={column.align} style={{...(column.rowStyle ?? {})}} onClick={(e) => {
                if (column?.isEditable) {
                    setEditModeStatus(true)
                }
                if (propsViewLink && column.dataKey !== 'action' && column.dataKey !== 'isActive' && column.dataKey !== 'report') {
                    e.stopPropagation(); navigate(`${propsViewLink}/${row?._id || row?.uuid}`);
                }
            }}>
                {column.renderer
                    ? column.renderer(value, row)
                    : value}
            </TableCell>
        )
    }

}


export default React.forwardRef(function StickyHeadTable({
                                                             rows = [],
                                                             columns = [],
                                                             handleChangePage: propHandleChangePage,
                                                             handleChangeRowsPerPage: propHandleChangeRowsPerPage,
                                                             count: propsCount,
                                                             rowsPerPage: propsRowsPerPage,
                                                             page: propsPage,
                                                             viewLink: propsViewLink,
                                                             hidePagination,
                                                         }, ref) {
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [rowData, setRowData] = React.useState([]);
    const [dirtyRows, setDirtyRows] = useState({})
    const previousRowData = useRef()


    const handleChangePage = (event, newPage) => {
        if (propHandleChangePage) {
            propHandleChangePage(event, newPage);
        } else {
            setPage(newPage);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        if (propHandleChangeRowsPerPage) {
            propHandleChangeRowsPerPage(event)
        } else {
            setRowsPerPage(+event.target.value);
            setPage(0);
        }
    };

    useEffect(() => {
        if (!_.isEqual(previousRowData?.current, rows)) {
            previousRowData.current = rows
            setRowData(rows)
        }
    }, [previousRowData?.current, rows])

    const handleDirtyRows = (id, row, key, updateValue) => {
        const updatedRow = _.set(_.cloneDeep(row), key, updateValue)
        setDirtyRows({...dirtyRows, [id]: updatedRow})
    }

    const removeDirtyRow = (id) => {
        const updatedDirtyRow = {...dirtyRows}
        delete updatedDirtyRow[id]
        setDirtyRows(updatedDirtyRow)
    }

    const getData = () => {
        return rowData.map((value) => {
            if (dirtyRows[value._id]) {
                return {
                    ...dirtyRows[value._id]
                }
            }
            return {
                ...value,
            }
        })
    }

    useImperativeHandle(ref, () => {
        return {
            removeDirtyRow,
            getData
        }
    }, [removeDirtyRow])

    const paginationData = useMemo(() => {
        const localRowsPerPage = propsRowsPerPage >= 0 ? propsRowsPerPage : rowsPerPage;
        const totalItems = propsCount >= 0 ? propsCount : rows.length;
        return {
            page: propsPage >= 0 ? propsPage : page,
            rowsPerPage: localRowsPerPage,
            count: totalItems,
        };
    }, [propsCount, propsRowsPerPage, propsPage, page, rowsPerPage, rows.length])

    return (
        <Paper sx={{width: '100%', overflow: 'hidden'}}>
            <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column.dataKey}
                                    align={column.align}
                                    style={{...(column.style ?? {})}}
                                >
                                    {column.label} {column?.infoIcon ?
                                        <Tooltip title={column?.infoTooltip || ""} arrow placement="top">
                                            <CustomInfoIcon fontSize="small" />
                                        </Tooltip>
                                    : ''}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows?.length > 0 ? rows.map((row) => {
                            return (
                                <TableRow className='cursor-pointer' hover role="checkbox" tabIndex={-1} key={row._id}>
                                    {columns.map((column) => {
                                        const currentRow = dirtyRows[row._id] || row
                                        return (
                                            <CustomTableCell defaultRow={row} column={column} row={currentRow}
                                                             propsViewLink={propsViewLink}
                                                             handleDirtyRows={handleDirtyRows}/>
                                        );
                                    })}
                                </TableRow>
                            );
                        }) : (
                                <TableRow
                                    tabIndex={-1}
                                    key={"no-row-available"}
                                >
                                    <TableCell
                                        rowSpan={"3"}
                                        colSpan={columns?.length}
                                        style={{textAlign: 'center', height: "100px"}}
                                    >
                                        No data available
                                    </TableCell>
                                </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {
                !hidePagination ?
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        {...paginationData}
                    />
                : null
            }
        </Paper>
    );
})
